import React, { useEffect } from "react";
import {
  ChoicesText,
  ExtraChargeText,
  SoldOutText,
  ChoiceNameRow,
  VarientCategoryContainer,
  Subtitle,
  Title,
  VariantHeaderContainer,
  VariantsContainer,
  VarientCategoryText,
  InputContainer,
  ChoicesBox,
  FieldErrorIconCustomContainer,
} from "./menu-variants.styles";
import { useSelector } from "react-redux";
import { selectOnlineOrdersConfigs } from "../../store/kitchen/kitchen.selector";
import { isOnlineOrderConfigValid } from "../../utils/utils.common";
import {
  resolveOptionCharge,
  resolveOptionExtraCharge,
} from "../../utils/variantPricing";
import { useTextColor } from "../../utils/colorContrast/useColorContrast";
import { FieldErrorIcon } from "../icons";
import { fieldError } from "../../constants";
import { useMediaQuery } from "@mui/material";

function removeSpecialCharacters(str) {
  return str.replace(/[^a-zA-Z0-9]/g, "").replace(/\s/g, "");
}

function formatSignedCharge(charge) {
  const value = Number(charge);
  if (!Number.isFinite(value) || value === 0) return "$0.00";
  const sign = value >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(value).toFixed(2)}`;
}

/** Base-price options are the item price, not an add-on — no leading "+". */
function formatOptionCharge(charge, { isBasePriceModifier = false } = {}) {
  const value = Number(charge);
  if (!Number.isFinite(value) || value === 0) return "$0.00";
  if (isBasePriceModifier) {
    if (value < 0) return `-$${Math.abs(value).toFixed(2)}`;
    return `$${value.toFixed(2)}`;
  }
  return formatSignedCharge(value);
}

export const MenuVariants = React.forwardRef(
  (
    {
      setDishPriceVariant,
      dishPriceVariant,
      maxCounter,
      minCounter,
      optionCategory,
      optionNumber,
      options,
      setVariantSelected,
      variantSelected,
      mandatoryChoice,
      setMinimumVariantsSelected,
      kitchenTier,
      brandTertiary,
      textBody,
      backgroundTertiary,
      headerModalBackground,
      backgroundSecondary,
      errorState,
      onClearError,
      isConditionalPricingModifier,
      isBasePriceModifier,
      basePriceOptionNameKey,
    },
    ref
  ) => {
    const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
    const group = { isConditionalPricingModifier };
    const isOptionalSingleSelect = maxCounter === 1 && (minCounter === 0 || minCounter === null);
    const isRequiredSingleSelect = maxCounter === 1 && minCounter === 1;

    const themeColor = useTextColor(headerModalBackground);
    const isMobile = useMediaQuery("(max-width: 768px)");

    function optionSelected(selection, i, type, isChecked = false) {
      // Resolve conditional pricing so the stored charge reflects the selected
      // base-price option and stays consistent in the cart.
      const effectiveCharge = resolveOptionCharge(
        selection,
        group,
        basePriceOptionNameKey
      );
      const resolvedSelection = {
        ...selection,
        charge: effectiveCharge,
        extraCharge: resolveOptionExtraCharge(effectiveCharge),
        ...(isBasePriceModifier ? { isBasePriceModifier: true } : {}),
      };

      setVariantSelected((prevState) => {
        let priceDelta = 0;
        let updatedState;

        if (type === "radio") {
          const prevSelected = prevState.find(
            (item) => item.optionCategory === optionCategory
          );

          if (prevSelected && prevSelected.extraCharge) {
            priceDelta -= Number(prevSelected.charge);
          }

          if (resolvedSelection.extraCharge) {
            priceDelta += Number(resolvedSelection.charge);
          }

          const filteredPrevState = prevState.filter(
            (item) => item.optionCategory !== optionCategory
          );

          updatedState = [
            ...filteredPrevState,
            {
              ...resolvedSelection,
              id: `${i}-${optionNumber}`,
              optionCategory,
              mandatoryChoice,
            },
          ];
        } else {
          // Checkbox logic
          if (isOptionalSingleSelect && isChecked) {
            const prevSelected = prevState.find(
              (item) => item.optionCategory === optionCategory
            );

            if (prevSelected && prevSelected.extraCharge) {
              priceDelta -= Number(prevSelected.charge);
            }

            if (resolvedSelection.extraCharge) {
              priceDelta += Number(resolvedSelection.charge);
            }

            const filteredPrevState = prevState.filter(
              (item) => item.optionCategory !== optionCategory
            );

            updatedState = [
              ...filteredPrevState,
              {
                ...resolvedSelection,
                id: `${i}-${optionNumber}`,
                optionCategory,
                mandatoryChoice,
              },
            ];
          } else if (!isChecked) {
            priceDelta -= resolvedSelection.extraCharge
              ? Number(resolvedSelection.charge)
              : 0;

            updatedState = prevState.filter(
              (item) => item.id !== `${i}-${optionNumber}`
            );
          } else {
            priceDelta += resolvedSelection.extraCharge
              ? Number(resolvedSelection.charge)
              : 0;

            updatedState = [
              ...prevState,
              {
                ...resolvedSelection,
                id: `${i}-${optionNumber}`,
                optionCategory,
                mandatoryChoice,
              },
            ];
          }
        }

        const selectedForThisOption = updatedState.filter(
          (item) => item.optionCategory === optionCategory
        );

        if (mandatoryChoice && selectedForThisOption.length >= minCounter) {
          onClearError?.();
        }

        if (priceDelta !== 0) {
          setDishPriceVariant((prevPrice) => prevPrice + priceDelta);
        }

        return updatedState;
      });
    }
    useEffect(() => {
      const mandatoryChoicesCount = variantSelected.filter(
        (item) => item.mandatoryChoice
      ).length;

      setMinimumVariantsSelected(mandatoryChoicesCount);
    }, [variantSelected, setMinimumVariantsSelected]);
    return (
      <VariantsContainer
        backgroundTertiary={backgroundTertiary}
        headerModalBackground={headerModalBackground}
        ref={ref}
      >
        <VariantHeaderContainer>
          <div
            style={{
              display: "flex",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <div>
              <Title brandTertiary={brandTertiary}>{optionCategory}</Title>
            </div>
          </div>
          {onlineOrdersConfig !== null &&
            onlineOrdersConfig?.isTyroLocationIdValid && (
              <div className="category__tag">
                {mandatoryChoice ? (
                  <VarientCategoryContainer className="required-varient">
                    <VarientCategoryText>Required</VarientCategoryText>
                  </VarientCategoryContainer>
                ) : (
                  <VarientCategoryContainer className="optional-varient">
                    <VarientCategoryText>Optional</VarientCategoryText>
                  </VarientCategoryContainer>
                )}
              </div>
            )}
        </VariantHeaderContainer>

        <Subtitle
          textBody={mandatoryChoice && errorState ? fieldError : textBody}
          mandatoryChoice={mandatoryChoice && errorState}
        >
          {minCounter === maxCounter && maxCounter === null && `Choose Any`}

          {minCounter === maxCounter &&
            maxCounter !== null &&
            `Exactly ${minCounter !== null ? minCounter : maxCounter}`}

          {minCounter !== 0 &&
            minCounter !== null &&
            maxCounter !== 0 &&
            maxCounter !== null &&
            minCounter !== maxCounter &&
            `Between ${minCounter} and ${maxCounter}`}

          {minCounter !== 0 &&
            (maxCounter === 0 || maxCounter === null) &&
            minCounter !== maxCounter &&
            `At least ${minCounter}`}

          {(minCounter === 0 || minCounter === null) &&
            maxCounter !== 0 &&
            minCounter !== maxCounter &&
            `Up to ${maxCounter}`}

          {mandatoryChoice && errorState && (
            <FieldErrorIconCustomContainer>
              <FieldErrorIcon size={isMobile ? "small" : "middle"} />
            </FieldErrorIconCustomContainer>
          )}
        </Subtitle>
        <div style={{ paddingTop: 16 }}>
          {options?.map((selections, i) => {
            const optionId = `${i}-${optionNumber}`;
            const isSelected = variantSelected.some(
              (item) => item.id === optionId
            );
            const isOptionSoldOut = selections?.isSoldOut === true;
            const selectedInCategoryCount = variantSelected.filter(
              (item) => item.optionCategory === optionCategory
            ).length;
            const isAtMaxForMultiSelect =
              !isOptionalSingleSelect &&
              !isRequiredSingleSelect &&
              maxCounter !== null &&
              maxCounter !== minCounter &&
              selectedInCategoryCount >= maxCounter;
            const isDisabled =
              isOptionSoldOut || (isAtMaxForMultiSelect && !isSelected);
            const effectiveCharge = resolveOptionCharge(
              selections,
              group,
              basePriceOptionNameKey
            );
            const showExtraCharge = resolveOptionExtraCharge(effectiveCharge);

            return onlineOrdersConfig !== null &&
              onlineOrdersConfig?.isTyroLocationIdValid ? (
              <fieldset
                key={i}
                style={{
                  borderWidth: "0px",
                  alignContent: "center",
                  display: "flex",
                  padding: "6px 0 6px",
                  margin: "0px",
                }}
              >
                <label style={{ display: "flex", width: "100%" }}>
                  {isOnlineOrderConfigValid(onlineOrdersConfig) ? (
                    <InputContainer
                      textBody={textBody}
                      backgroundSecondary={backgroundSecondary}
                      themeColor={themeColor}
                    >
                      <input
                        id={`${removeSpecialCharacters(selections?.name || "")}-${optionNumber}-${i}`}
                        type={
                          isRequiredSingleSelect ? "radio" : "checkbox"
                        }
                        name={`action-${optionNumber}`}
                        value="track"
                        className={`checkbox checkbox-${optionNumber}`}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={(e) =>
                          !isOptionSoldOut &&
                          optionSelected(
                            selections,
                            i,
                            isRequiredSingleSelect ? "radio" : "checkbox",
                            e.target.checked
                          )
                        }
                        size="0px"
                        style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                      />
                    </InputContainer>
                  ) : (
                    <div>-</div>
                  )}
                  <div className={`checkbox-${optionNumber}`} style={{ display: "flex", flexBasis: "100%" }}>
                    <ChoicesBox>
                      <ChoiceNameRow>
                        <ChoicesText textBody={textBody} soldOut={isOptionSoldOut}>
                          {selections?.name}{" "}
                        </ChoicesText>
                        {isOptionSoldOut && <SoldOutText>Sold Out</SoldOutText>}
                      </ChoiceNameRow>
                      {showExtraCharge && (
                        <ExtraChargeText textBody={textBody}>
                          {formatOptionCharge(effectiveCharge, {
                            isBasePriceModifier,
                          })}
                        </ExtraChargeText>
                      )}
                    </ChoicesBox>
                  </div>
                </label>
              </fieldset>
            ) : (
              <ul style={{ paddingLeft: 0, listStyle: "none" }}>
                <li style={{ paddingTop: 6, paddingBottom: 6 }}>
                  <ChoicesBox>
                    <ChoicesText textBody={textBody}>
                      {selections?.name}{" "}
                    </ChoicesText>
                    {showExtraCharge && (
                      <ExtraChargeText textBody={textBody}>
                        {formatOptionCharge(effectiveCharge, {
                          isBasePriceModifier,
                        })}
                      </ExtraChargeText>
                    )}
                  </ChoicesBox>
                </li>
              </ul>
            );
          })}
        </div>
        {/* <hr /> */}
      </VariantsContainer>
    );
  }
);
